// ********************************************************************************************
// ********************************************************************************************
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken, type Provider } from '@angular/core';
// This is a service that uses the Angular Hono client to make requests to the Dealcore API.
// And this is the only exception for eslint-disable-next-line @nx/enforce-module-boundaries!!!
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { DealcoreType } from '@hyundai/dealcore-lib/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Observable } from 'rxjs';
import { state } from '../+state/login/login.reducer';
import { injectPartnerWebUrl } from '../providers/partner-web-url.token';
import { isHWS } from '../providers/platform.token';
import { injectAngularHonoClient } from '../utilities/ng-hono-client';
import { DEALCORE_API_CONFIG } from './dealer-api.service';

export interface UserApiConfig {
    baseUrl: string;
}

export const USER_API_CONFIG = new InjectionToken<UserApiConfig>('USER_API_CONFIG');

export function provideUserApiConfig(config: UserApiConfig): Provider {
    return { provide: USER_API_CONFIG, useValue: config };
}
@Injectable({ providedIn: 'root' })
export class UserApiService {
    protected readonly store = inject(Store);
    protected readonly authToken = this.store.selectSignal(state.selectAuthToken);

    protected readonly http = inject(HttpClient);
    protected readonly apiConfig = inject(DEALCORE_API_CONFIG);
    protected readonly api = injectAngularHonoClient<DealcoreType>({
        url: this.apiConfig.baseUrl,
        authToken: this.authToken,
        withCredentials: true,
    });
    protected readonly partnerWebUrl = injectPartnerWebUrl();
    protected readonly isHWS = isHWS();

    protected postAuth<T>(url: string, body: FormData | Record<string, unknown>): Observable<T> {
        return this.http.post<T>(`${this.apiConfig.baseUrl}/${url}`, body, {
            headers: this.authToken()
                ? {
                      Authorization: `Bearer ${this.authToken()}`,
                  }
                : undefined,
        });
    }

    async sendLoginCode(email: string) {
        // Return this.api.login.sendCode.$post({ json: { email } }).then((res) => res.json());
        const request = await this.api.login.sendCode.$post({ json: { email } });
        return request.json();
    }

    async verifyLoginCode(email: string, code: string) {
        // Return this.api.login.verifyCode.$post({ json: { email, code } }).then((res) => res.json());
        const request = await this.api.login.verifyCode.$post({ json: { email, code } });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async isAuthenticated() {
        const request = await this.api.login.isAuthenticated.$post();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getUserInfo() {
        const request = await this.api.login.userInfo.$get();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getDealerSlug() {
        const request = await this.api.login.dealerSlug.$get();
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getFormId(name: string) {
        const request = await this.api.form[':name'].$get({ param: { name } });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getPartnerWebJwt() {
        if (this.isHWS) {
            return null;
        }
        const request = await firstValueFrom(
            this.http.get<{ jwt: string | null }>(`${this.partnerWebUrl}/api/jwt`, {
                withCredentials: true,
            }),
        );

        return request;
    }
}
